// ============================================================
// Analytics v2 — event schema (single source of truth)
//
// One logical "events" table. The Analytics Engine writer and the
// D1/SQLite store both derive their layout from COLUMNS, and the query
// layer only ever references logical column names. YOM-PLATFORM-PLAN.md §4.
// ============================================================
import { encodePrivacyFlags } from './privacy.js';
/** Logical string columns, in Analytics Engine blob order (blob1…blob20). */
export const BLOB_COLUMNS = [
    'sub_tenant',
    'event',
    'host',
    'path',
    'referrer',
    'channel',
    'utm_source',
    'utm_campaign',
    'country',
    'device',
    'browser',
    'os',
    'session',
    'visitor',
    'item_id',
    'label',
    'd1',
    'd2',
    'd3',
    'd4',
];
/** Logical numeric columns, in Analytics Engine double order (double1…double20). */
export const DOUBLE_COLUMNS = [
    'value',
    'qty',
    'duration_ms',
    'consent',
    'internal',
    'server',
    'event_ts',
    /** Privacy evidence flags (privacy.ts): consent, GPC/DNT, what YOM did. */
    'reserved',
    'm1',
    'm2',
    'm3',
    'm4',
    'm5',
    'm6',
    'm7',
    'm8',
    'm9',
    'm10',
    'm11',
    'm12',
];
export const CUSTOM_DIMENSION_SLOTS = 4;
export const CUSTOM_METRIC_SLOTS = 12;
/** AE dataset for v2 events (must match wrangler.toml). */
export const AE_DATASET_V2 = 'yom_events_v2';
/** Max data points a single Worker invocation may write to Analytics Engine. */
export const AE_MAX_POINTS_PER_INVOCATION = 250;
/** Analytics Engine data point for an event (index = project). */
export function toAEDataPoint(ev) {
    const row = toRow(ev);
    return {
        indexes: [ev.projectId],
        blobs: BLOB_COLUMNS.map(c => String(row[c] ?? '')),
        doubles: DOUBLE_COLUMNS.map(c => Number(row[c] ?? 0)),
    };
}
/** Flat logical row (column name → value), used by the D1 store and tests. */
export function toRow(ev) {
    const metrics = Array.from({ length: CUSTOM_METRIC_SLOTS }, (_, i) => ev.metrics[i] ?? 0);
    return {
        sub_tenant: ev.subTenant,
        event: ev.event,
        host: ev.host,
        path: ev.path,
        referrer: ev.referrer,
        channel: ev.channel,
        utm_source: ev.utm.source,
        utm_campaign: ev.utm.campaign,
        country: ev.country,
        device: ev.device,
        browser: ev.browser,
        os: ev.os,
        session: ev.session,
        visitor: ev.visitor,
        item_id: ev.itemId,
        label: ev.label,
        d1: ev.dims[0],
        d2: ev.dims[1],
        d3: ev.dims[2],
        d4: ev.dims[3],
        value: ev.value,
        qty: ev.qty,
        duration_ms: ev.durationMs,
        consent: ev.consent === 'granted' ? 1 : 0,
        internal: ev.internal ? 1 : 0,
        server: ev.source === 'server' ? 1 : 0,
        event_ts: ev.ts,
        reserved: encodePrivacyFlags(ev.consent, ev.privacy),
        m1: metrics[0],
        m2: metrics[1],
        m3: metrics[2],
        m4: metrics[3],
        m5: metrics[4],
        m6: metrics[5],
        m7: metrics[6],
        m8: metrics[7],
        m9: metrics[8],
        m10: metrics[9],
        m11: metrics[10],
        m12: metrics[11],
    };
}
/** AE SQL column for a logical column. */
export function aeColumn(col) {
    if (col === 'project_id') {
        return 'index1';
    }
    if (col === 'timestamp') {
        return 'timestamp';
    }
    const b = BLOB_COLUMNS.indexOf(col);
    if (b >= 0) {
        return `blob${b + 1}`;
    }
    const d = DOUBLE_COLUMNS.indexOf(col);
    if (d >= 0) {
        return `double${d + 1}`;
    }
    throw new Error(`unknown column ${col}`);
}
export function isStringColumn(col) {
    return col === 'project_id' || BLOB_COLUMNS.includes(col);
}
export function isNumericColumn(col) {
    return DOUBLE_COLUMNS.includes(col);
}
/** DDL for the D1/SQLite events table (local dev, self-host, tests). */
export function eventsTableDDL(table = 'events') {
    const blobs = BLOB_COLUMNS.map(c => `${c} TEXT NOT NULL DEFAULT ''`);
    const doubles = DOUBLE_COLUMNS.map(c => `${c} REAL NOT NULL DEFAULT 0`);
    return [
        `CREATE TABLE IF NOT EXISTS ${table} (
            id TEXT NOT NULL,
            project_id TEXT NOT NULL,
            timestamp INTEGER NOT NULL,
            ${[...blobs, ...doubles].join(',\n            ')}
        )`,
        `CREATE INDEX IF NOT EXISTS idx_${table}_project_ts ON ${table}(project_id, timestamp)`,
        `CREATE INDEX IF NOT EXISTS idx_${table}_project_st_ts ON ${table}(project_id, sub_tenant, timestamp)`,
    ];
}
//# sourceMappingURL=schema.js.map